import { z } from 'zod'

// The backend validates structure only; the registered table definition decodes the contents.
export const TableConfigurationJsonSchema = z.record(z.string(), z.json())

export const StoredTableConfigurationSchema = z.object({
  columns: TableConfigurationJsonSchema,
  query: TableConfigurationJsonSchema,
})

export const TableConfigurationAudienceModeSchema = z.enum(['owner', 'selected', 'all'])

export const TableConfigurationAudienceSummarySchema = z.object({
  mode: TableConfigurationAudienceModeSchema,
  role_count: z.number(),
  user_count: z.number(),
})

export const GetDefaultTableConfigurationSchema = z.object({
  configuration: StoredTableConfigurationSchema,
  configuration_updated_at: z.string(),
  id: z.number(),
  schema_version: z.number(),
  updated_at: z.string(),
})

export const GetSavedViewSummarySchema = z.object({
  audience_summary: TableConfigurationAudienceSummarySchema,
  available: z.boolean(),
  configuration_updated_at: z.string(),
  created_at: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  id: z.number(),
  is_owner: z.boolean(),
  is_pinned: z.boolean(),
  name: z.string(),
  organisation_applicability: z.object({
    organisations: z.array(z.object({ id: z.number(), name: z.string() })),
    type: z.enum(['all', 'selected']),
  }),
  owner: z.object({ id: z.number(), last_name: z.string().nullable(), name: z.string() }),
  shared_via: z.array(z.enum(['user', 'role', 'all'])),
  updated_at: z.string(),
})

export const GetSavedViewDetailSchema = GetSavedViewSummarySchema.extend({
  configuration: StoredTableConfigurationSchema,
  schema_version: z.number(),
})

export const GetSavedViewAudienceEntrySchema = z.discriminatedUnion('type', [
  z.object({
    active: z.boolean(),
    id: z.number(),
    last_name: z.string().nullable(),
    name: z.string(),
    type: z.literal('user'),
  }),
  z.object({ id: z.number(), name: z.string(), type: z.literal('role') }),
])

export const GetSavedViewRecoveryItemSchema = z.object({
  audience_summary: TableConfigurationAudienceSummarySchema,
  configuration_updated_at: z.string(),
  created_at: z.string(),
  id: z.number(),
  name: z.string(),
  owner: z.object({
    active: z.boolean(),
    id: z.number(),
    last_name: z.string().nullable(),
    name: z.string(),
  }),
  updated_at: z.string(),
})

export type DefaultTableConfiguration = z.infer<typeof GetDefaultTableConfigurationSchema>
export type SavedViewAudienceEntry = z.infer<typeof GetSavedViewAudienceEntrySchema>
export type SavedViewDetail = z.infer<typeof GetSavedViewDetailSchema>
export type SavedViewRecoveryItem = z.infer<typeof GetSavedViewRecoveryItemSchema>
export type SavedViewSummary = z.infer<typeof GetSavedViewSummarySchema>
export type StoredTableConfiguration = z.infer<typeof StoredTableConfigurationSchema>
export type TableConfigurationJson = z.infer<typeof TableConfigurationJsonSchema>
